import sharedSupabase from '../config/supabase';
import { buildAllocationLine } from '../utils/allocationMath';
import { buildNeedsFromCountItems } from './purchaseAllocationService';
import { fetchEquivalentGroupComparisons } from './itemRelationshipService';
import { hasVendorChoices } from '../utils/purchasePlanning';
import { buildSameItemGroups, WORKING_TYPES } from '../utils/relatedItemGroups';
import {
  buildShoppingCandidateGraph,
} from '../../../supabase/functions/_shared/shoppingCandidates.mjs';
import { requestComparisonRecompute } from './comparisonRecomputeService';
import { convertBasisQuantity } from '../utils/packageBasis';
import { planBillingBasisClassification } from '../utils/billingBasis';

/**
 * Purchasing plan service.
 *
 * A "purchasing plan" is a purchase_allocation seen from the Purchasing side:
 * the count said what is needed, and the plan is the resumable work of
 * deciding where to buy it. It exists between those two questions and stays
 * editable the whole time — only the final PO review confirms.
 *
 * Nothing here creates purchase orders. Confirming is the single moment that
 * does, and it lives in purchaseAllocationService.confirmAllocation behind the
 * confirm_purchase_allocation RPC.
 */

/** Plan states that are still open for editing. Mirrors the status CHECK. */
export const OPEN_PLAN_STATUSES = Object.freeze([
  'draft',
  'needs_vendor_review',
  'ready_for_po_review',
]);

export const PLAN_STATUS = Object.freeze({
  DRAFT: 'draft',
  NEEDS_VENDOR_REVIEW: 'needs_vendor_review',
  READY_FOR_PO_REVIEW: 'ready_for_po_review',
  CONFIRMED: 'confirmed',
  CANCELED: 'canceled',
});

/** Which screen a plan should open on. */
export const PLAN_STEP = Object.freeze({
  OPTIMIZE: 'optimize',
  PO_REVIEW: 'po_review',
  CONFIRMED: 'confirmed',
});

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

/**
 * A hard deadline on a write the UI is visibly waiting for. Supabase requests
 * ride on fetch, which has no timeout of its own — a stalled connection or a
 * hung RPC left "Working…" buttons busy forever, with no way back but a
 * reload. The write may still land after this fires; the message says so, and
 * every caller is an idempotent RPC or a plain UPDATE, so "reload to check,
 * then retry" is always safe advice.
 */
export const WRITE_TIMEOUT_MS = 45000;
const withWriteDeadline = (promise, what, timeoutMs = WRITE_TIMEOUT_MS) => {
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(
        `${what} is taking longer than expected. It may still finish on the server — reload the page to check before trying again.`,
      ));
    }, timeoutMs);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
};

const UNIQUE_VIOLATION = '23505';

// Stores behind on 20260804120000 have neither the new statuses nor plan_mode.
// They must still be able to plan — they just cannot record which mode or step
// they were on, so they always resume at the routing decision.
const isMissingColumnError = (error) =>
  !!error && (error.code === '42703' || error.code === 'PGRST204');
const isBadStatusError = (error) =>
  !!error && (error.code === '23514' || String(error.message || '').includes('status_check'));
// A store still on the older RPC signature has no p_expected_revision. PostgREST
// answers "function ... does not exist" (PGRST202) for the unknown overload.
const isMissingFunctionArgError = (error) =>
  !!error && (error.code === 'PGRST202' || /does not exist/i.test(String(error.message || '')));

/**
 * The store's confirmed Equivalent working-set groups, for count identity,
 * catalog folding, and primary purchase-option pooling.
 *
 * Imports record "same product from another vendor" as a confirmed
 * item_relationships pair instead of merging items, so an item's real
 * alternatives often live on sibling items. This fetches only what routing
 * needs (the id pairs) and projects them into groups.
 *
 * Failure posture: ANY problem — table not deployed, RLS, network — returns
 * null, and every caller then plans item-scoped exactly as before. The
 * relationship graph is an improvement to the routing question, never a way
 * for plan creation to fail.
 */
export async function fetchWorkingSetGroupsForStore(storeId, client = sharedSupabase) {
  if (!storeId) return null;
  try {
    const { data, error } = await client
      .from('item_relationships')
      .select('item_a_id, item_b_id, relationship_type, status')
      .eq('store_id', storeId)
      .in('relationship_type', [...WORKING_TYPES])
      .eq('status', 'confirmed');
    if (error) throw new Error(error.message);
    return buildSameItemGroups(data || []);
  } catch (err) {
    console.warn('[purchasing-plan] item relationships unavailable, planning item-scoped:', err?.message || err);
    return null;
  }
}

// The pre-widening name; same fetch, kept for callers written against it.
export const fetchSameItemGroupsForStore = fetchWorkingSetGroupsForStore;

/**
 * Shopping Review's broader graph: confirmed Equivalent closures plus direct
 * confirmed Alternative neighbours. This does NOT widen WORKING_TYPES and is
 * never used by count/card identity. The pure expansion itself lives in the
 * shared shoppingCandidates module used by the Edge comparison path too.
 */
export async function fetchShoppingCandidateGraphForStore(storeId, client = sharedSupabase) {
  if (!storeId) return null;
  try {
    const { data, error } = await client
      .from('item_relationships')
      .select('id, item_a_id, item_b_id, relationship_type, status, confidence, reasoning, material_differences, substitution_notes')
      .eq('store_id', storeId)
      .in('relationship_type', ['same_item', 'purchasing_alternative'])
      .eq('status', 'confirmed');
    if (error) throw new Error(error.message);
    return buildShoppingCandidateGraph(data || []);
  } catch (err) {
    console.warn('[purchasing-plan] shopping relationships unavailable, planning Equivalent-only:', err?.message || err);
    return null;
  }
}

/**
 * The open plan for a count session, if there is one.
 * At most one can exist — a partial unique index enforces it.
 */
export async function fetchOpenPlanForCount(storeId, countSessionId, client = sharedSupabase) {
  if (!storeId || !countSessionId) return null;
  const { data, error } = await client
    .from('purchase_allocations')
    .select('*')
    .eq('store_id', storeId)
    .eq('count_session_id', countSessionId)
    .in('status', OPEN_PLAN_STATUSES)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  throwIfError(error);
  return data || null;
}

/**
 * Creates the draft plan for a completed count, or returns the one already
 * there.
 *
 * Idempotent by construction, in two layers. It looks for an open plan first,
 * and if two requests race past that check the partial unique index rejects
 * the loser, whose 23505 is treated as "someone else just made it" and
 * re-read. Pressing "Generate POs" twice, or double-submitting on a slow
 * connection, therefore yields one plan rather than two competing ones — which
 * would later become two sets of purchase orders for the same count.
 *
 * @returns {Promise<{ plan, lines, created: boolean }>}
 */
export async function createOrOpenPlanForCount({
  storeId,
  countSessionId,
  countItems = [],
  optionsByItemId = {},
  shoppingCandidateGraph = null,
  userId = null,
} = {}, client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!countSessionId) throw new Error('countSessionId is required');

  const existing = await fetchOpenPlanForCount(storeId, countSessionId, client);
  if (existing) {
    const lines = await fetchPlanLines(existing.id, client);
    return { plan: existing, lines, created: false };
  }

  // Groups come FIRST so the need builder itself is group-aware: a confirmed
  // Equivalent group is ONE purchasing need whether or not the count screen's
  // display fold ran before these rows arrived. null groups (fetch failed or
  // nothing confirmed) degrades to the old item-scoped needs.
  const shoppingGraph = shoppingCandidateGraph
    || await fetchShoppingCandidateGraphForStore(storeId, client);
  const groups = shoppingGraph?.equivalentGroups
    || await fetchSameItemGroupsForStore(storeId, client);
  let stickyCanonicalByGroupId;
  if (groups?.membersByGroupId?.size) {
    try {
      const groupComparisons = await fetchEquivalentGroupComparisons(storeId, client);
      stickyCanonicalByGroupId = new Map(
        Object.entries(groupComparisons || {})
          .filter(([, row]) => row?.canonical_item_id != null)
          .map(([groupKey, row]) => [String(groupKey), String(row.canonical_item_id)]),
      );
    } catch (_) {
      stickyCanonicalByGroupId = undefined;
    }
  }
  const needs = buildNeedsFromCountItems(countItems, {
    optionsByItemId,
    groups,
    stickyCanonicalByGroupId,
  });
  const status = needs.length && hasVendorChoices(
    needs.map((need) => ({ id: null, inventory_item_id: need.inventory_item_id })),
    optionsByItemId,
    { storeId, groups, shoppingCandidateGraph: shoppingGraph },
  )
    ? PLAN_STATUS.NEEDS_VENDOR_REVIEW
    : PLAN_STATUS.READY_FOR_PO_REVIEW;

  const insert = async (withStatus) => client
    .from('purchase_allocations')
    .insert({
      store_id: storeId,
      count_session_id: countSessionId,
      status: withStatus,
      created_by: userId ?? null,
    })
    .select()
    .single();

  let { data: plan, error } = await insert(status);
  // A store behind on the migration rejects the new statuses; plain 'draft'
  // still describes the plan correctly, it just cannot record the step.
  if (isBadStatusError(error)) ({ data: plan, error } = await insert(PLAN_STATUS.DRAFT));
  if (error?.code === UNIQUE_VIOLATION) {
    const raced = await fetchOpenPlanForCount(storeId, countSessionId, client);
    if (raced) {
      const lines = await fetchPlanLines(raced.id, client);
      return { plan: raced, lines, created: false };
    }
  }
  throwIfError(error);

  const lines = await createPlanLines({ plan, storeId, needs, optionsByItemId }, client);
  return { plan, lines, created: true };
}

// purchase_allocation_lines has no columns for the display-only comparison
// facts buildAllocationLine produces; they reach the screen through the option
// join instead. Same contract as purchaseAllocationService.
const DISPLAY_ONLY_LINE_FIELDS = [
  'description', 'purchase_unit', 'quantity_per_package', 'package_price',
  'comparison_unit', 'comparison_quantity_per_package', 'comparable_unit_cost',
  'comparison_source', 'comparison_confidence', 'comparison_estimated',
  // Layered order decision (raw need → item target → driver) — derived facts
  // frozen into option_snapshot by the confirm trigger, never line columns.
  'raw_need_quantity', 'purchase_target_quantity', 'target_driver', 'order_driver',
  // ORDER is not BILLING: whether a line's total is settled on a scale, and
  // the rate it settles at ("$16.40/LB × ~60 LB"). Derived from the option's
  // billing basis on every read — never a line column.
  'cost_is_estimated', 'billing_basis_text',
];

const forStorage = (line) => {
  const row = { ...line };
  DISPLAY_ONLY_LINE_FIELDS.forEach((field) => { delete row[field]; });
  return row;
};

const forStorageWithRecommendedBaseline = (line) => {
  const row = forStorage(line);
  return {
    ...row,
    recommended_purchase_option_id: row.selected_purchase_option_id ?? null,
    recommended_selection_reason: row.selection_reason ?? null,
  };
};

async function createPlanLines({ plan, storeId, needs, optionsByItemId }, client) {
  if (!needs.length) return [];
  const rows = needs.map((item) => ({
    allocation_id: plan.id,
    store_id: storeId,
    ...forStorageWithRecommendedBaseline(
      buildAllocationLine({ item, options: optionsByItemId?.[item.inventory_item_id] || [] }),
    ),
  }));

  let { data, error } = await client.from('purchase_allocation_lines').insert(rows).select();
  if (error && isMissingColumnError(error)) {
    const stripped = rows.map(({
      selection_reason: _r,
      recommended_purchase_option_id: _recommendedOption,
      recommended_selection_reason: _recommendedReason,
      ...rest
    }) => rest);
    ({ data, error } = await client.from('purchase_allocation_lines').insert(stripped).select());
  }
  throwIfError(error);
  return data || [];
}

/** Lines for a plan, each with its selected option joined for display. */
export async function fetchPlanLines(planId, client = sharedSupabase) {
  if (!planId) return [];
  const { data, error } = await client
    .from('purchase_allocation_lines')
    // The embed names its foreign key on purpose. purchase_allocation_lines
    // has had TWO paths to purchase_options since
    // 20260824120000_purchase_plan_recommended_baseline added
    // recommended_purchase_option_id beside selected_purchase_option_id, and an
    // unqualified purchase_options(...) is answered with 300/PGRST201 rather
    // than a guess. `option` is the line's CURRENT choice, so it is the
    // selected-option constraint; the recommendation is a separate baseline
    // that display never resolves through this join.
    .select('*, option:purchase_options!purchase_allocation_lines_selected_purchase_option_id_fkey(*)')
    .eq('allocation_id', planId)
    .order('created_at', { ascending: true });
  throwIfError(error);
  return data || [];
}

/**
 * Best-effort PAR provenance for a plan's items, from the count session that
 * created it: the normalized PAR, what was counted, and which vendor package
 * the PAR was expressed against. The worksheet shows these ONLY when they
 * reconcile with the frozen quantity_needed, so a count edited after plan
 * creation can never contradict the need on screen. Fails soft to {} — PAR
 * provenance is an explanation, never a requirement.
 *
 * @returns {Promise<Object<string, {parLevel:number, countedOnHand:number|null,
 *   parSourceOptionId:string|null}>>} inventory_item_id → PAR facts
 */
export async function fetchParContextForPlan(plan, client = sharedSupabase) {
  const sessionId = plan?.count_session_id;
  if (!sessionId) return {};
  const BASE = 'inventory_item_id, is_counted, current_count, par_normalized_quantity';
  const OPTIONAL = ', par_purchase_option_id';
  const run = (columns) => client
    .from('count_items')
    .select(columns)
    .eq('count_session_id', sessionId);
  let { data, error } = await run(BASE + OPTIONAL);
  if (error) ({ data, error } = await run(BASE));
  if (error || !Array.isArray(data)) return {};

  const byItemId = {};
  data.forEach((row) => {
    const itemId = row?.inventory_item_id;
    if (itemId == null) return;
    const key = String(itemId);
    const entry = byItemId[key] || (byItemId[key] = {
      parLevel: null, countedOnHand: null, parSourceOptionId: null, sawCount: false,
    });
    const par = Number(row.par_normalized_quantity);
    if (Number.isFinite(par) && par >= 0 && (entry.parLevel === null || par > entry.parLevel)) {
      entry.parLevel = par;
      entry.parSourceOptionId = row.par_purchase_option_id != null
        ? String(row.par_purchase_option_id)
        : entry.parSourceOptionId;
    }
    const counted = Number(row.current_count);
    const isCounted = !!row.is_counted
      || (row.current_count !== '' && row.current_count != null);
    if (isCounted && Number.isFinite(counted)) {
      entry.countedOnHand = (entry.countedOnHand ?? 0) + counted;
      entry.sawCount = true;
    }
  });

  const result = {};
  Object.entries(byItemId).forEach(([itemId, entry]) => {
    if (entry.parLevel === null) return;
    result[itemId] = {
      parLevel: entry.parLevel,
      countedOnHand: entry.sawCount ? entry.countedOnHand : null,
      parSourceOptionId: entry.parSourceOptionId,
    };
  });
  return result;
}

/** One plan with its lines. */
export async function fetchPlan(planId, client = sharedSupabase) {
  if (!planId) return null;
  const { data: plan, error } = await client
    .from('purchase_allocations')
    .select('*')
    .eq('id', planId)
    .maybeSingle();
  throwIfError(error);
  if (!plan) return null;
  return { ...plan, lines: await fetchPlanLines(planId, client) };
}

/**
 * Open plans for a store, newest first — the Purchasing "needs review" list.
 * Line counts ride along so the list can say "24 items" without a second pass.
 */
export async function fetchOpenPlans(storeId, client = sharedSupabase) {
  if (!storeId) return [];
  let { data, error } = await client
    .from('purchase_allocations')
    .select('*, lines:purchase_allocation_lines(id, extended_cost, selected_purchase_option_id)')
    .eq('store_id', storeId)
    .in('status', OPEN_PLAN_STATUSES)
    .order('created_at', { ascending: false });
  // Pre-migration stores only know 'draft'.
  if (error) {
    ({ data, error } = await client
      .from('purchase_allocations')
      .select('*, lines:purchase_allocation_lines(id, extended_cost, selected_purchase_option_id)')
      .eq('store_id', storeId)
      .eq('status', PLAN_STATUS.DRAFT)
      .order('created_at', { ascending: false }));
  }
  throwIfError(error);

  return (data || []).map((plan) => {
    const lines = plan.lines || [];
    return {
      ...plan,
      lines: undefined,
      itemCount: lines.length,
      estimatedTotal: lines.reduce((sum, l) => sum + (Number(l.extended_cost) || 0), 0),
      unassignedCount: lines.filter((l) => !l.selected_purchase_option_id).length,
    };
  });
}

/**
 * Open plans for many count sessions in one query, keyed by session id.
 * Lets a list of completed counts badge "Savings review waiting" without a
 * per-row round trip.
 *
 * @returns {Promise<Object<string, object>>} count_session_id → plan row
 */
export async function fetchOpenPlansForSessions(storeId, countSessionIds, client = sharedSupabase) {
  if (!storeId || !Array.isArray(countSessionIds) || countSessionIds.length === 0) return {};
  let { data, error } = await client
    .from('purchase_allocations')
    .select('id, count_session_id, status, created_at')
    .eq('store_id', storeId)
    .in('count_session_id', countSessionIds)
    .in('status', OPEN_PLAN_STATUSES);
  if (error) {
    ({ data, error } = await client
      .from('purchase_allocations')
      .select('id, count_session_id, status, created_at')
      .eq('store_id', storeId)
      .in('count_session_id', countSessionIds)
      .eq('status', PLAN_STATUS.DRAFT));
  }
  if (error) return {};
  const map = {};
  (data || []).forEach((plan) => { map[plan.count_session_id] = plan; });
  return map;
}

/**
 * Where a plan should resume.
 *
 * Driven by the plan's own status rather than recomputed from options, so a
 * user who has already settled their vendor choices is not dropped back into
 * optimization because a price changed while they were away. The one
 * recomputation is for plans that predate the status model: they have no
 * recorded step, so the vendor-choice question is asked fresh.
 */
export function resolvePlanStep(plan, {
  lines = [], optionsByItemId = {}, storeId = null, groups = null,
} = {}) {
  if (!plan) return null;
  if (plan.status === PLAN_STATUS.CONFIRMED) return PLAN_STEP.CONFIRMED;
  if (plan.status === PLAN_STATUS.READY_FOR_PO_REVIEW) return PLAN_STEP.PO_REVIEW;
  if (plan.status === PLAN_STATUS.NEEDS_VENDOR_REVIEW) return PLAN_STEP.OPTIMIZE;

  // 'draft' — a plan from before the status model, or a store behind on the
  // migration. Decide the same way creation would have (callers that have
  // fetched same_item groups pass them so both decisions use the same rule).
  return hasVendorChoices(lines, optionsByItemId, { storeId, groups })
    ? PLAN_STEP.OPTIMIZE
    : PLAN_STEP.PO_REVIEW;
}

/**
 * Persists the optimization result and moves the plan to PO review.
 *
 * Explicitly NOT a confirm. Reaching PO review must leave the plan editable —
 * the user can come back, change modes, and change vendors right up until they
 * create the orders.
 */
export async function saveOptimizationAndAdvance({
  planId,
  mode = null,
  preferredVendorId = null,
  selections = [],
} = {}, client = sharedSupabase) {
  if (!planId) throw new Error('planId is required');

  for (const selection of selections) {
    if (!selection?.lineId) continue;
    const patch = {
      selected_purchase_option_id: selection.optionId ?? null,
      selection_reason: selection.reason ?? null,
      packages_required: selection.packages ?? null,
      quantity_received: selection.quantityReceived ?? null,
      overage: selection.overage ?? null,
      unit_cost: selection.unitCost ?? null,
      extended_cost: selection.orderCost ?? null,
      vendor_id: selection.vendorId ?? null,
    };
    let { error } = await client
      .from('purchase_allocation_lines')
      .update(patch)
      .eq('id', selection.lineId);
    if (error && isMissingColumnError(error)) {
      const { selection_reason: _r, ...stripped } = patch;
      ({ error } = await client
        .from('purchase_allocation_lines')
        .update(stripped)
        .eq('id', selection.lineId));
    }
    throwIfError(error);
  }

  const patch = {
    status: PLAN_STATUS.READY_FOR_PO_REVIEW,
    plan_mode: mode,
    preferred_vendor_id: preferredVendorId,
  };
  let { data, error } = await client
    .from('purchase_allocations')
    .update(patch)
    .eq('id', planId)
    .select()
    .single();
  if (error && (isMissingColumnError(error) || isBadStatusError(error))) {
    // Pre-migration store: the selections above are what matter and they are
    // already saved. The step simply cannot be recorded.
    ({ data, error } = await client
      .from('purchase_allocations')
      .select('*')
      .eq('id', planId)
      .single());
  }
  throwIfError(error);
  return data;
}

/** Sends a plan back to optimization from PO review. */
export async function returnToOptimization(planId, client = sharedSupabase) {
  if (!planId) throw new Error('planId is required');
  const { data, error } = await client
    .from('purchase_allocations')
    .update({ status: PLAN_STATUS.NEEDS_VENDOR_REVIEW })
    .eq('id', planId)
    .in('status', OPEN_PLAN_STATUSES)
    .select()
    .maybeSingle();
  throwIfError(error);
  return data;
}

/**
 * Persists one drag: the line now buys the target vendor's option, priced
 * that vendor's way. Reuses the same repricing as the picker path so a drag
 * and a dropdown selection cannot disagree about the math.
 *
 * @returns {Promise<object>} the updated line row
 */
export async function moveLineToOption(lineId, option, client = sharedSupabase) {
  if (!lineId) throw new Error('lineId is required');
  if (!option?.id) throw new Error('option is required');

  const { updateLineOption } = await import('./purchaseAllocationService');
  const updated = await updateLineOption(lineId, option.id, option, client);

  // updateLineOption predates vendor-aware lines; the worksheet groups by the
  // option's vendor, so keep the line's vendor_id in step for readers that
  // trust the column (the RPC re-stamps it authoritatively at fan-out).
  if (option.vendor_id != null) {
    const { error } = await client
      .from('purchase_allocation_lines')
      .update({ vendor_id: option.vendor_id })
      .eq('id', lineId)
      .is('fanned_out_at', null);
    if (error && !isMissingColumnError(error)) throwIfError(error);
  }
  return { ...updated, vendor_id: option.vendor_id ?? updated?.vendor_id ?? null };
}

/**
 * Commits recommendations that are already visible in one vendor's column
 * but whose allocation line still has no persisted option.
 *
 * A package repair or late comparison can make an option eligible after the
 * allocation was created. buildPurchasePlan then shows the real recommendation
 * (and includes it in the vendor subtotal), while fan-out correctly reads only
 * `purchase_allocation_lines.selected_purchase_option_id`. Finish is the
 * buyer's explicit commitment to the displayed arrangement, so it persists
 * those newly eligible recommendations first and hands the resulting line
 * snapshot to the ordinary preflight barrier.
 *
 * Only NULL selections are filled. A different non-null selection is a stale
 * screen/race and remains untouched for preflight to reject.
 */
export async function persistVisibleVendorSelections({
  order,
  persistedLines = [],
  optionsById,
  persistSelection = moveLineToOption,
} = {}) {
  const lookupOption = (id) => {
    if (id == null) return null;
    if (optionsById instanceof Map) return optionsById.get(String(id)) || optionsById.get(id) || null;
    return optionsById?.[String(id)] || optionsById?.[id] || null;
  };
  let nextLines = [...(persistedLines || [])];
  let persistedCount = 0;

  for (const visibleLine of order?.lines || []) {
    const lineId = visibleLine?.lineId ?? visibleLine?.id ?? null;
    const selectedOptionId = visibleLine?.selectedOptionId
      ?? visibleLine?.selected_purchase_option_id
      ?? null;
    if (lineId == null || selectedOptionId == null) continue;
    const current = nextLines.find((line) => String(line?.id) === String(lineId));
    if (!current) {
      const failure = new Error('This purchase plan changed before the order was created.');
      failure.code = 'savings_review_changed';
      failure.changedLineIds = [String(lineId)];
      throw failure;
    }
    if (current.selected_purchase_option_id != null) continue;
    const option = lookupOption(selectedOptionId);
    if (!option) {
      const failure = new Error('The recommended vendor package is no longer available. Reload the Purchase Planner and choose again.');
      failure.code = 'savings_review_changed';
      failure.changedLineIds = [String(lineId)];
      throw failure;
    }
    // Sequential by design: every write recalculates and revision-stamps one
    // allocation line. A later failure creates no order; any earlier writes
    // remain authoritative and the normal stale-review path can reload them.
    // eslint-disable-next-line no-await-in-loop
    const updated = await persistSelection(lineId, option);
    nextLines = nextLines.map((line) => (
      String(line?.id) === String(lineId) ? { ...line, ...(updated || {}) } : line
    ));
    persistedCount += 1;
  }

  return { lines: nextLines, persistedCount };
}

// Maps complete_allocation_vendor failures to messages a buyer can act on.
const friendlyCompleteVendorError = (message) => {
  const text = String(message || '');
  if (text.includes('not found')) return 'This purchase plan no longer exists.';
  if (text.includes('does not have access')) return 'You do not have access to this store.';
  if (text.includes('only open allocations')) return 'This purchase plan is already finished.';
  if (text.includes('No lines')) return 'Nothing in this vendor’s column to order.';
  if (text.includes('is not active')) return 'A product’s purchase option is no longer active. Move it to another vendor and try again.';
  // The plan moved while the order was being created — say so plainly rather
  // than leaving a raw SQLSTATE message at the bottom of a tall column.
  if (text.includes('allocation_revision_stale')) {
    return 'This purchase plan changed while the order was being created. Nothing was created — refresh the Purchase Planner and finish again.';
  }
  // Name the product. "need_unit_incompatible_with_package_basis" tells a
  // buyer nothing; which product to fix, and how, is actionable.
  if (text.includes('need_unit_incompatible_with_package_basis')) {
    const item = text.match(/Package needs review for "([^"]+)"/)?.[1];
    return `${item ? `“${item}”` : 'A product'} does not say how much of your counting unit one package holds, so its order quantity cannot be calculated. Confirm its package on the card, or move it to another vendor.`;
  }
  if (text.includes('Package needs review')) {
    const item = text.match(/Package needs review for "([^"]+)"/)?.[1];
    return `${item ? `“${item}”` : 'A product'} has a package that needs confirming before it can be ordered — confirm it on the card in this review, then finish again.`;
  }
  return text || 'Could not create the order for this vendor.';
};

/**
 * Order-bearing line facts whose persisted value must match the worksheet at
 * Finish. Identifiers are compared as text and quantities as stored values;
 * this is a drift detector, not an arithmetic engine.
 */
const ORDER_AFFECTING_LINE_FIELDS = [
  'selected_purchase_option_id',
  'vendor_id',
  'quantity_needed',
  'packages_required',
  'extended_cost',
  'fanned_out_at',
];

const comparableLineValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (field === 'selected_purchase_option_id' || field === 'vendor_id' || field === 'fanned_out_at') {
    return String(value);
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : String(value);
};

/** Line ids whose order-bearing persisted facts differ between two reads. */
export function changedOrderLineIds(before = [], after = []) {
  const beforeById = new Map((before || []).map((line) => [String(line.id), line]));
  const afterById = new Map((after || []).map((line) => [String(line.id), line]));
  const ids = new Set([...beforeById.keys(), ...afterById.keys()]);
  return [...ids].filter((id) => {
    const left = beforeById.get(id);
    const right = afterById.get(id);
    if (!left || !right) return true;
    return ORDER_AFFECTING_LINE_FIELDS.some((field) => (
      comparableLineValue(field, left[field]) !== comparableLineValue(field, right[field])
    ));
  });
}

/**
 * Finishes one vendor's column: freezes snapshots for its lines and creates
 * that vendor's draft PO (delivery) or shopping list (pickup) via the
 * complete_allocation_vendor RPC. Idempotent — re-finishing a finished vendor
 * returns the ids created the first time. When the last buyable line fans
 * out, the RPC confirms the whole allocation.
 *
 * @returns {Promise<{ purchaseOrderId, shoppingListId, alreadyCompleted, allocationStatus }>}
 */
export async function completeVendorForPlan(planId, vendorId, client = sharedSupabase, expectedRevision = null) {
  if (!planId) throw new Error('planId is required');
  if (!vendorId) throw new Error('vendorId is required');
  const args = {
    p_allocation_id: planId,
    p_vendor_id: vendorId,
  };
  // State which arrangement is being finished. If the plan moved on between
  // the review being read and Finish being pressed, the RPC refuses rather
  // than turning an arrangement nobody is looking at into an order. Older
  // deployments have no such argument, so it is only sent when known and a
  // missing-parameter error retries without it.
  const hasExpectedRevision = expectedRevision !== null
    && expectedRevision !== undefined
    && (typeof expectedRevision !== 'string' || expectedRevision.trim() !== '');
  if (hasExpectedRevision && Number.isFinite(Number(expectedRevision))) {
    args.p_expected_revision = Number(expectedRevision);
  }
  let { data, error } = await withWriteDeadline(
    client.rpc('complete_allocation_vendor', args),
    'Creating this order',
  );
  if (error && args.p_expected_revision != null && isMissingFunctionArgError(error)) {
    delete args.p_expected_revision;
    ({ data, error } = await withWriteDeadline(
      client.rpc('complete_allocation_vendor', args),
      'Creating this order',
    ));
  }
  if (error) {
    const failure = new Error(friendlyCompleteVendorError(error.message));
    if (String(error.message || '').includes('allocation_revision_stale')) {
      failure.code = 'allocation_revision_stale';
    }
    failure.cause = error;
    throw failure;
  }
  const parsed = typeof data === 'string' ? JSON.parse(data) : data || {};
  return {
    purchaseOrderId: parsed.purchase_order_id ?? null,
    shoppingListId: parsed.shopping_list_id ?? null,
    alreadyCompleted: parsed.already_completed === true,
    allocationStatus: parsed.allocation_status ?? null,
  };
}

/**
 * Finish barrier: read the authoritative plan and lines, prove the worksheet
 * still shows the same order-bearing facts, then compare-and-set the exact
 * revision from that read. A mutation between the read and RPC is deliberately
 * not retried: complete_allocation_vendor must reject that genuine race.
 */
export async function completeVendorAfterPreflight(
  planId,
  vendorId,
  displayedLines = [],
  client = sharedSupabase,
) {
  const persistedPlan = await fetchPlan(planId, client);
  if (!persistedPlan) throw new Error('This purchase plan no longer exists.');

  const changedLineIds = changedOrderLineIds(displayedLines, persistedPlan.lines);
  if (changedLineIds.length) {
    const failure = new Error('This purchase plan changed before the order was created.');
    failure.code = 'savings_review_changed';
    failure.changedLineIds = changedLineIds;
    throw failure;
  }

  const revisionPresent = persistedPlan.revision !== null
    && persistedPlan.revision !== undefined
    && String(persistedPlan.revision).trim() !== '';
  const expectedRevision = Number(persistedPlan.revision);
  if (!revisionPresent || !Number.isInteger(expectedRevision) || expectedRevision < 0) {
    throw new Error('Countertop could not verify the current version of this purchase plan, so nothing was created. Reload the Purchase Planner and try again.');
  }

  const result = await completeVendorForPlan(
    planId,
    vendorId,
    client,
    expectedRevision,
  );
  return { result, persistedPlan };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The savings review a purchase order came out of, or null.
 *
 * `metadata.allocation_id` is stamped by complete_allocation_vendor and
 * confirm_purchase_allocation, and it is the same link their own idempotent
 * replay reads. Checked for shape here so a hand-edited or legacy metadata
 * blob cannot put a "return it" button on a PO that has nowhere to go back to.
 */
export function savingsReviewIdForPurchaseOrder(purchaseOrder) {
  const raw = purchaseOrder?.metadata?.allocation_id;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return UUID_RE.test(trimmed) ? trimmed : null;
}

/**
 * The still-unsent draft POs a plan has already produced, keyed by vendor id.
 *
 * This is what makes a finished column reversible from the worksheet itself:
 * the vendor is locked, and the buyer needs to know whether there is a draft
 * behind that lock that can be handed back. A vendor whose order has been sent
 * has no entry, because a sent order is not returnable.
 *
 * Never throws — a worksheet that cannot list drafts still has to render, it
 * just cannot offer the undo.
 *
 * @returns {Promise<Object<string, object>>} vendor id → purchase order row
 */
export async function fetchReturnableDraftsForPlan(planId, client = sharedSupabase) {
  if (!planId) return {};
  const { data, error } = await client
    .from('purchase_orders')
    .select('id, po_number, vendor_id, vendor_name_snapshot, delivery_status, total, line_count, created_at')
    .eq('metadata->>allocation_id', planId)
    .in('delivery_status', ['draft', 'blocked_no_email'])
    .is('sent_at', null)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[purchasingPlanService] Could not list returnable drafts:', error.message);
    return {};
  }
  const byVendor = {};
  (data || []).forEach((po) => {
    if (po.vendor_id == null) return;
    const key = String(po.vendor_id);
    // Newest first from the query, so the first one seen is the current draft.
    if (!byVendor[key]) byVendor[key] = po;
  });
  return byVendor;
}

// Maps return_po_to_savings_review failures to messages a buyer can act on.
const friendlyReturnError = (message) => {
  const text = String(message || '');
  if (text.includes('not found')) return 'This purchase order no longer exists.';
  if (text.includes('does not have access')) return 'You do not have access to this store.';
  if (text.includes('only an unsent draft')) return 'This order has already been sent, so it cannot go back to the Purchase Planner. Cancel it or send a revision instead.';
  if (text.includes('did not come from a savings review')) return 'This order was not created from a purchase plan, so there is nothing to return it to.';
  if (text.includes('no longer exists')) return 'The purchase plan this order came from no longer exists.';
  if (text.includes('was canceled')) return 'The purchase plan this order came from was canceled.';
  if (text.includes('another savings review open')) return 'That count already has another purchase plan open. Finish or cancel it before returning this draft.';
  if (text.includes('has no vendor')) return 'This order has no vendor, so there is no column to return its items to.';
  return text || 'Could not return this draft to the Purchase Planner.';
};

/**
 * Sends ONE unsent draft purchase order back to the worksheet that created it
 * — the inverse of completeVendorForPlan, and the only way out of a finished
 * column. The draft is deleted, its allocation lines lose fanned_out_at and
 * option_snapshot (so they are draggable again and re-price from the live
 * options), and a review that had confirmed itself reopens. Other vendors'
 * columns are untouched.
 *
 * @returns {Promise<{ allocationId, vendorId, poNumber, returnedLineCount, allocationStatus }>}
 */
export async function returnPurchaseOrderToPlan(purchaseOrderId, client = sharedSupabase) {
  if (!purchaseOrderId) throw new Error('purchaseOrderId is required');
  const { data, error } = await withWriteDeadline(
    client.rpc('return_po_to_savings_review', {
      p_purchase_order_id: purchaseOrderId,
    }),
    'Returning this draft',
  );
  if (error) throw new Error(friendlyReturnError(error.message));
  const parsed = typeof data === 'string' ? JSON.parse(data) : data || {};
  return {
    allocationId: parsed.allocation_id ?? null,
    vendorId: parsed.vendor_id ?? null,
    poNumber: parsed.po_number ?? null,
    returnedLineCount: Number(parsed.returned_line_count) || 0,
    allocationStatus: parsed.allocation_status ?? null,
  };
}

/**
 * Persists the decisive billing-basis classifications a savings review is
 * about to price by, so the database's own arithmetic (the allocation-line
 * freeze trigger, purchase_option_billing_economics) reads the same verdict
 * the browser prices with. Without this, an unclassified catch-weight row is
 * priced two ways at once: the review shows the pack-evidence estimate
 * (~$296 for 1 pack × ~32 LB × $9.25/LB) while the freeze recomputes
 * packages × package_price ($9.25) — the review-vs-PO mismatch.
 *
 * planBillingBasisClassification (billingBasis.mjs — the one classifier)
 * decides WHAT is decisive; this function only applies its patch, guarded so
 * a concurrent human decision or price edit always wins: the UPDATE matches
 * only a still-active, still-unclassified row whose package_price is the one
 * the classification read. The server-side refresh trigger then re-prices any
 * open allocation lines selecting the option in the same transaction, so the
 * caller reloads and shows numbers the fan-out will agree with.
 *
 * Returns the number of options actually classified; 0 means nothing needed
 * it (the overwhelmingly common case — fixed packages never match).
 */
export async function classifyPlanBillingBases(
  optionsByItemId = {},
  itemsById = {},
  client = sharedSupabase,
) {
  const plans = [];
  for (const [itemId, options] of Object.entries(optionsByItemId || {})) {
    const item = itemsById?.[itemId] ?? null;
    for (const option of options || []) {
      const plan = planBillingBasisClassification(option, { item });
      if (plan) plans.push(plan);
    }
  }
  if (!plans.length) return 0;

  let applied = 0;
  for (const plan of plans) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await withWriteDeadline(
      client
        .from('purchase_options')
        .update(plan.patch)
        .eq('id', plan.optionId)
        .eq('status', 'active')
        .is('fulfillment_model', null)
        .eq('package_price', plan.guard.packagePrice)
        .select('id'),
      'Recording how this vendor bills',
    );
    // A guard miss (someone classified, re-priced, or retired the option in
    // the meantime) is not an error — their answer stands.
    if (error) throw new Error(error.message);
    if (Array.isArray(data) && data.length > 0) applied += 1;
  }
  return applied;
}

/**
 * Persists a buyer's correction to a package — the back of the worksheet card
 * and the "Package needs confirmation" repair state. This IS the human
 * confirmation the interpretation pipeline trusts above every parser and
 * model: qty_per_package updates, the cost recomputes from it, and the
 * snapshot records source 'confirmed' so no future parser change can
 * silently reinterpret this package.
 *
 * Optional facts, each only written when the user actually answered:
 *   packagePrice  what one purchase unit costs, as the vendor bills it —
 *                 asked, never prefilled from a divided case price.
 *   purchaseUnit  the order-unit word the confirmation names ('case' when
 *                 the buyer said "1 case = 4 bags = 48 rolls").
 *
 * Stale caches heal in the same write: inner-pack columns that contradict
 * the confirmed contents are cleared (the interpretation snapshot preserves
 * the originals for audit), and a pack description that described a
 * DIFFERENT package is rewritten to the confirmed facts — an inner-bag
 * option must not keep the outer case's wording. The relationship comparison
 * is queued for recompute so the new fingerprint gets stamped.
 */
export async function confirmOptionPackageFacts(
  optionId,
  {
    qtyPerPackage,
    packagePrice = null,
    purchaseUnit = null,
    quantityUnit = null,
    interpretation = null,
  } = {},
  client = sharedSupabase,
) {
  if (!optionId) throw new Error('optionId is required');
  const qty = Number(qtyPerPackage);
  if (!Number.isFinite(qty) || qty <= 0) throw new Error('qtyPerPackage must be a positive number');
  const confirmedPrice = packagePrice === null || packagePrice === undefined || packagePrice === ''
    ? null
    : Number(packagePrice);
  if (confirmedPrice !== null && (!Number.isFinite(confirmedPrice) || confirmedPrice <= 0)) {
    throw new Error('packagePrice, when given, must be a positive number');
  }

  const { data: option, error: fetchError } = await withWriteDeadline(
    client
      .from('purchase_options')
      .select('id, store_id, inventory_item_id, vendor_sku, vendor_description, package_price, purchase_unit, pack_description, normalized_count_unit, inner_pack_count, inner_pack_size, inner_pack_size_unit, inner_pack_unit, conversion_snapshot')
      .eq('id', optionId)
      .single(),
    'Saving the package confirmation',
  );
  throwIfError(fetchError);

  const price = confirmedPrice ?? Number(option?.package_price);
  const unitWord = String(purchaseUnit ?? option?.purchase_unit ?? '').trim() || null;
  const countUnit = String(quantityUnit ?? option?.normalized_count_unit ?? '').trim() || null;

  const innerCount = Number(option?.inner_pack_count);
  const innerSize = Number(option?.inner_pack_size);
  const structuredTotal = Number.isFinite(innerCount) && innerCount > 0
    && Number.isFinite(innerSize) && innerSize > 0
    ? innerCount * innerSize
    : null;
  const structuredInCountUnit = structuredTotal !== null
    ? convertBasisQuantity(
      structuredTotal,
      option?.inner_pack_size_unit ?? countUnit,
      countUnit,
    )
    : null;
  const structuredAgrees = structuredInCountUnit === null || Math.abs(structuredInCountUnit - qty)
    <= Math.max(1e-6, Math.max(structuredInCountUnit, qty) * 1e-6);

  const existingMeasurements = Array.isArray(option?.conversion_snapshot?.package_measurements)
    ? option.conversion_snapshot.package_measurements
    : [];
  const packageMeasurements = [...existingMeasurements];
  if (structuredTotal !== null && option?.inner_pack_size_unit) {
    packageMeasurements.push({
      quantity: structuredTotal,
      unit: option.inner_pack_size_unit,
      role: 'vendor_pack',
      source: 'vendor_pack',
      confirmed: true,
    });
  }
  packageMeasurements.push({
    quantity: qty,
    unit: countUnit,
    role: 'inventory_bridge',
    source: 'confirmed',
    confirmed: true,
    confidence: 1,
  });
  const semantic = interpretation && typeof interpretation === 'object'
    ? interpretation
    : option?.conversion_snapshot?.package_interpretation?.interpretation;
  const semanticMeasuredTotal = Number(semantic?.measured_total_quantity);
  if (Number.isFinite(semanticMeasuredTotal) && semanticMeasuredTotal > 0 && semantic?.measured_total_unit) {
    packageMeasurements.push({
      quantity: semanticMeasuredTotal,
      unit: semantic.measured_total_unit,
      role: 'vendor_pack',
      source: 'confirmed',
      confirmed: true,
      confidence: 1,
    });
  }
  const semanticPerInner = Number(semantic?.quantity_per_inner_pack);
  if (Number.isFinite(semanticPerInner) && semanticPerInner > 0 && semantic?.quantity_unit) {
    packageMeasurements.push({
      quantity: semanticPerInner,
      unit: semantic.quantity_unit,
      role: 'per_inner',
      source: 'confirmed',
      confirmed: true,
      confidence: 1,
    });
  }
  const dedupedMeasurements = [...new Map(packageMeasurements.map((measurement) => {
    const key = [
      String(measurement?.role || 'contents').toLowerCase(),
      String(measurement?.unit || '').toLowerCase(),
      Math.round(Number(measurement?.quantity) * 1e6) / 1e6,
    ].join('|');
    return [key, measurement];
  })).values()];

  const snapshot = {
    ...(option?.conversion_snapshot || {}),
    package_measurements: dedupedMeasurements,
    package_interpretation: (() => {
      const prior = (option?.conversion_snapshot || {}).package_interpretation || {};
      const confirmedAt = new Date().toISOString();
      const confirmationHistory = Array.isArray(prior.confirmation_history)
        ? [...prior.confirmation_history]
        : [];
      if (prior.interpretation || prior.model || prior.trust_decision) {
        confirmationHistory.push({
          semantic_interpretation: prior.interpretation || null,
          model: prior.model || null,
          trust_decision: prior.trust_decision || null,
          raw: prior.raw || null,
          confirmed_at: confirmedAt,
          confirmed_package_price: Number.isFinite(price) && price > 0 ? price : null,
          quantity_in_item_count_unit: qty,
          item_count_unit: countUnit,
        });
      }
      return {
      ...prior,
      ...(interpretation && typeof interpretation === 'object' ? interpretation : {}),
      order_unit: unitWord,
      quantity_in_item_count_unit: qty,
      item_count_unit: countUnit,
      source: 'confirmed',
      requires_review: false,
      confirmed_at: confirmedAt,
      confirmed_package_price: Number.isFinite(price) && price > 0 ? price : null,
      confirmed_implied_unit_cost: Number.isFinite(price) && price > 0
        ? Math.round((price / qty) * 1e6) / 1e6
        : null,
      raw: prior.raw || {
        pack_description: String(option?.pack_description || '').trim().toLowerCase() || null,
        purchase_unit: String(unitWord || '').trim().toLowerCase() || null,
        vendor_description: String(option?.vendor_description || '').trim().toLowerCase() || null,
      },
      vendor_sku: option?.vendor_sku || null,
      semantic_interpretation: prior.interpretation || prior.semantic_interpretation || null,
      semantic_model: prior.model || prior.semantic_model || null,
      confirmation_history: confirmationHistory,
      // Audit trail for anything this confirmation displaced.
      ...(structuredTotal !== null && !structuredAgrees
        ? {
          displaced_inner_pack: {
            inner_pack_count: option?.inner_pack_count ?? null,
            inner_pack_size: option?.inner_pack_size ?? null,
            inner_pack_size_unit: option?.inner_pack_size_unit ?? null,
          },
        }
        : {}),
      ...(confirmedPrice !== null && Number(option?.package_price) !== confirmedPrice
        ? { displaced_package_price: option?.package_price ?? null }
        : {}),
      };
    })(),
  };

  const patch = {
    qty_per_package: qty,
    ...(countUnit ? { normalized_count_unit: countUnit } : {}),
    normalized_unit_cost: Number.isFinite(price) && price > 0
      ? Math.round((price / qty) * 1e6) / 1e6
      : null,
    conversion_snapshot: snapshot,
    conversion_status: 'valid',
  };
  if (confirmedPrice !== null) patch.package_price = confirmedPrice;
  if (purchaseUnit != null && String(purchaseUnit).trim()) patch.purchase_unit = String(purchaseUnit).trim();
  if (structuredTotal !== null && !structuredAgrees) {
    // The structured columns described a different package (the outer case's
    // 4 × 12 against a confirmed 12-roll bag). They are a cache of the
    // snapshot, which now says otherwise — clear them rather than leave a
    // stored contradiction. Same rule for the pack description: an
    // inner-package option must not keep the whole case's wording.
    patch.inner_pack_count = null;
    patch.inner_pack_size = null;
    patch.inner_pack_size_unit = null;
    patch.inner_pack_unit = null;
    if (option?.pack_description) {
      snapshot.package_interpretation.raw_pack_description = option.pack_description;
      patch.pack_description = countUnit
        ? `${qty} ${countUnit} per ${unitWord || 'package'}`
        : option.pack_description;
    }
  }

  const { data, error } = await withWriteDeadline(
    client
      .from('purchase_options')
      .update(patch)
      .eq('id', optionId)
      .select()
      .single(),
    'Saving the package confirmation',
  );
  throwIfError(error);

  // The confirmed package is a new fingerprint; the stored comparison is
  // stale until the recompute stamps it. Best-effort, never blocks the save.
  try {
    if (option?.store_id != null && option?.inventory_item_id != null) {
      void requestComparisonRecompute(option.store_id, option.inventory_item_id, client);
    }
  } catch {
    // Comparison freshness is a nicety; the confirmation stands either way.
  }
  return data;
}

/**
 * Abandons a plan. Canceling frees the count to be planned again, since the
 * one-open-plan index only covers open statuses.
 */
export async function cancelPlan(planId, client = sharedSupabase) {
  if (!planId) throw new Error('planId is required');
  const { data, error } = await client
    .from('purchase_allocations')
    .update({ status: PLAN_STATUS.CANCELED })
    .eq('id', planId)
    .in('status', OPEN_PLAN_STATUSES)
    .select()
    .maybeSingle();
  throwIfError(error);
  return data;
}
